import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type Database, type Client, type DBServer, type Node, humanBytes } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, ErrorBox } from '../components/ui'
import { IconPlus } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

export function DatabasesPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: dbs = [] } = useQuery({ queryKey: ['databases'], queryFn: async () => (await api.get<Database[]>('/databases')).data })
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: async () => (await api.get<Client[]>('/clients')).data })
  const { data: servers = [] } = useQuery({ queryKey: ['db-servers'], queryFn: async () => (await api.get<DBServer[]>('/db-servers')).data })
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]))
  const serverMap = Object.fromEntries(servers.map(s => [s.id, s]))
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  const [fNode, setFNode] = useState('')
  const [fClient, setFClient] = useState(0)
  const [fSearch, setFSearch] = useState('')
  const filtered = useMemo(() => {
    let list = dbs
    if (fClient) list = list.filter(d => d.client_id === fClient)
    if (fNode) {
      const clientIDsOnNode = new Set(clients.filter(c => c.node_id === fNode).map(c => c.id))
      list = list.filter(d => clientIDsOnNode.has(d.client_id))
    }
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(d => d.name.toLowerCase().includes(q))
    }
    return list
  }, [dbs, fNode, fClient, fSearch, clients])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/databases/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['databases'] }),
  })
  const dump = useMutation({
    mutationFn: async (id: number) => (await api.post<{ task_id: string }>(`/databases/${id}/dump`)).data,
    onSuccess: (r) => setTaskId(r.task_id),
  })
  const [tblFor, setTblFor] = useState<Database | null>(null)

  return (
    <>
      <PageHeader
        title="Базы данных"
        subtitle="MySQL / MariaDB. Сначала установите базу на ноде: Ноды → Установить → База данных."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Создать базу</Button>}
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Имя базы…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
        <FilterSelect value={fClient} onChange={setFClient} options={[
          { value: 0, label: 'Все клиенты' },
          ...clients.map(c => ({ value: c.id, label: c.unix_user })),
        ]} />
      </FilterBar>
      <Table<Database>
        rows={filtered} empty={dbs.length === 0 ? 'Пока нет баз данных.' : 'Ничего не найдено.'}
        onRowClick={(d) => setTblFor(d)}
        cols={[
          { key: 'n', label: 'Имя', render: (d) => <span className="font-mono font-medium">{d.name}</span> },
          { key: 'nd', label: 'Нода', render: (d) => {
            const c = clientMap[d.client_id]
            return <span className="text-[13px] text-neutral-700">{c ? (nodeMap[c.node_id]?.name || '—') : '—'}</span>
          }},
          { key: 'c', label: 'Клиент', render: (d) => <span className="font-mono text-[12px]">{clientMap[d.client_id]?.unix_user || d.client_id}</span> },
          { key: 's', label: 'Сервер', render: (d) => <span className="text-[13px] text-neutral-600">{serverMap[d.db_server_id]?.type || '—'} · {serverMap[d.db_server_id]?.version || ''}</span> },
          { key: 'ch', label: 'Кодировка', render: (d) => <span className="text-[12px] text-neutral-500">{d.charset} / {d.collation_name}</span> },
          { key: 'sz', label: 'Размер', render: (d) => <span className="text-[12px] text-neutral-600">{humanBytes(d.size_bytes)}</span> },
          { key: 'act', label: '', w: '1%', render: (d) => (
            <div className="flex justify-end">
              <RowMenu items={[
                { label: 'Таблицы + размеры', onClick: () => setTblFor(d) },
                { label: 'phpMyAdmin', onClick: async () => {
                  const token = localStorage.getItem('beebro.token') || ''
                  // 1) открываем окно с pma-прокси (ставит cookie) — URL пока пустой
                  const w = window.open('about:blank', '_blank')
                  if (!w) { alert('Снять блокировку всплывающих окон'); return }
                  // 2) грузим HTML с авто-POST формой
                  const res = await fetch(`/api/v1/databases/${d.id}/pma-login`, { headers: { Authorization: `Bearer ${token}` }})
                  let html = await res.text()
                  // подменяем action: добавляем ?token= в первый клик
                  html = html.replace(/action="([^"]+)"/, (_m, a) => `action="${a}?token=${encodeURIComponent(token)}"`)
                  w.document.write(html)
                  w.document.close()
                }},
                { label: 'Дамп (.sql.gz)', onClick: () => dump.mutate(d.id) },
                { divider: true, label: '', onClick: () => {} },
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить базу ${d.name}?`) && del.mutate(d.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {tblFor && <TablesModal db={tblFor} onClose={() => setTblFor(null)} />}
      {addOpen && <AddDBModal clients={clients} servers={servers} onClose={() => setAddOpen(false)}
        onTask={(t) => { setAddOpen(false); setTaskId(t); qc.invalidateQueries({ queryKey: ['databases'] }) }} />}
      {taskId && <TaskProgress taskId={taskId} onClose={() => setTaskId(null)} />}
    </>
  )
}

function AddDBModal({ clients, servers, onClose, onTask }: { clients: Client[]; servers: DBServer[]; onClose: () => void; onTask: (id: string) => void }) {
  const [f, setF] = useState({ client_id: clients[0]?.id || 0, db_server_id: 0, name: '', charset: 'utf8mb4', collation: 'utf8mb4_unicode_ci' })
  // серверы на ноде выбранного клиента
  const client = clients.find(c => c.id === f.client_id)
  const nodeServers = servers.filter(s => client && s.node_id === client.node_id)
  const currentSrv = nodeServers.find(s => s.id === f.db_server_id) || nodeServers[0]
  const isPg = currentSrv?.type === 'postgres'
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/databases', f)).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Создать базу данных">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Клиент">
          <Select value={f.client_id} onChange={(e) => setF({ ...f, client_id: +e.target.value })}>
            {clients.map(c => <option key={c.id} value={c.id}>{c.unix_user}</option>)}
          </Select>
        </Field>
        <Field label="Сервер БД">
          <Select value={f.db_server_id || (currentSrv?.id || 0)} onChange={(e) => setF({ ...f, db_server_id: +e.target.value })}>
            {nodeServers.length === 0 && <option value="0">— нет серверов на этой ноде —</option>}
            {nodeServers.map(s => <option key={s.id} value={s.id}>{s.type} {s.version} — {s.host}:{s.port}</option>)}
          </Select>
        </Field>
        <Field label="Имя базы"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={isPg ? 'myapp' : 'myapp'} required autoFocus /></Field>
        {!isPg && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Кодировка"><Input value={f.charset} onChange={(e) => setF({ ...f, charset: e.target.value })} /></Field>
            <Field label="Collation"><Input value={f.collation} onChange={(e) => setF({ ...f, collation: e.target.value })} /></Field>
          </div>
        )}
        {isPg && (
          <Field label="Encoding"><Input value={f.charset === 'utf8mb4' ? 'UTF8' : f.charset} onChange={(e) => setF({ ...f, charset: e.target.value })} placeholder="UTF8" /></Field>
        )}
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Создаём…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}

interface TableSize { name: string; rows: number; data_bytes: number; index_bytes: number }

function TablesModal({ db, onClose }: { db: Database; onClose: () => void }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ['db-tables', db.id],
    queryFn: async () => (await api.get<TableSize[]>(`/databases/${db.id}/tables`)).data,
  })
  const total = data.reduce((s, t) => s + t.data_bytes + t.index_bytes, 0)
  return (
    <Modal open onClose={onClose} title={`Таблицы: ${db.name}`} size="lg">
      {isLoading ? <div className="p-8 text-center text-neutral-400">Загрузка…</div> : (
        <div className="border border-neutral-200 rounded-md overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50/60 border-b border-neutral-100 text-[11px] uppercase text-neutral-500 tracking-wider">
              <tr>
                <th className="text-left font-medium px-4 py-2">Таблица</th>
                <th className="text-right font-medium px-4 py-2">Строк</th>
                <th className="text-right font-medium px-4 py-2">Данные</th>
                <th className="text-right font-medium px-4 py-2">Индексы</th>
                <th className="text-right font-medium px-4 py-2">Всего</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Пустая база</td></tr>}
              {data.map(t => (
                <tr key={t.name} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/40">
                  <td className="px-4 py-2 font-mono">{t.name}</td>
                  <td className="px-4 py-2 text-right font-mono">{t.rows.toLocaleString('ru-RU')}</td>
                  <td className="px-4 py-2 text-right font-mono text-neutral-600">{humanBytes(t.data_bytes)}</td>
                  <td className="px-4 py-2 text-right font-mono text-neutral-600">{humanBytes(t.index_bytes)}</td>
                  <td className="px-4 py-2 text-right font-mono font-medium">{humanBytes(t.data_bytes + t.index_bytes)}</td>
                </tr>
              ))}
              {data.length > 0 && (
                <tr className="bg-neutral-50/60 font-medium">
                  <td colSpan={4} className="px-4 py-2 text-right text-neutral-500 text-[12px]">Итого</td>
                  <td className="px-4 py-2 text-right font-mono">{humanBytes(total)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end pt-4">
        <Button variant="ghost" onClick={onClose}>Закрыть</Button>
      </div>
    </Modal>
  )
}
